// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslations } from "next-intl";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { VN_PHONE_REGEX } from "@chotca/config";
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  FormField,
  Input,
  PhoneInput,
  Skeleton,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
  useToast,
} from "@chotca/ui";
import { Users } from "lucide-react";
import { ErrorState } from "@/components/error-state";
import { useMe } from "@chotca/api-client";
import { useAddMember, useMembers, useRemoveMember } from "@chotca/api-client";

// `phone` is collected for parity with the existing UI but is not part of the
// add-member payload (HR members are invited by email).
const addMemberFormSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(1, "Vui lòng nhập họ và tên")
    .max(120, "Họ và tên tối đa 120 ký tự"),
  phone: z
    .string()
    .trim()
    .regex(
      VN_PHONE_REGEX,
      "SĐT không hợp lệ. Nhập số Việt Nam bắt đầu bằng 0 hoặc +84.",
    ),
  email: z.string().trim().email("Email không hợp lệ"),
  jobTitle: z.string().trim().max(120, "Chức danh tối đa 120 ký tự"),
});

type AddMemberFormValues = z.infer<typeof addMemberFormSchema>;

// Adding extra HR members is disabled for now (1 account = 1 doanh nghiệp).
// Listing + revoke stay available; flip to re-enable the add flow.
const SHOW_ADD_MEMBER = false;

export function MembersClient() {
  const t = useTranslations("orgSettings");
  const me = useMe();
  const membership = me.data?.memberships[0];
  const orgId = membership?.orgId;
  const isOwner = membership?.role === "OWNER";
  const members = useMembers(orgId);
  const add = useAddMember(orgId ?? "");
  const remove = useRemoveMember(orgId ?? "");
  const { toast } = useToast();

  const [openAdd, setOpenAdd] = useState(false);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<AddMemberFormValues>({
    resolver: zodResolver(addMemberFormSchema),
    mode: "onTouched",
    defaultValues: { fullName: "", phone: "", email: "", jobTitle: "" },
  });

  function openAddDialog() {
    reset({ fullName: "", phone: "", email: "", jobTitle: "" });
    setOpenAdd(true);
  }

  const submit = handleSubmit(async (values) => {
    try {
      await add.mutateAsync({
        email: values.email,
        fullName: values.fullName,
        jobTitle: values.jobTitle || undefined,
        role: "HR",
      });
      setOpenAdd(false);
    } catch (e) {
      toast({
        tone: "error",
        title: e instanceof Error && e.message ? e.message : t("saveError"),
      });
    }
  });

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <h1 className="text-display-lg font-extrabold text-ink">
          {t("membersTitle")}
        </h1>
        {SHOW_ADD_MEMBER && isOwner ? (
          <button
            type="button"
            onClick={openAddDialog}
            className="rounded-full bg-ink px-5 py-2 text-sm font-semibold text-paper hover:bg-ink/90"
          >
            {t("memberAddCta")}
          </button>
        ) : null}
      </header>

      <nav className="flex flex-wrap gap-2">
        <Link
          to="/org"
          className="rounded-full border border-smoke px-4 py-1.5 text-sm font-semibold text-ink hover:bg-mist"
        >
          {t("tabInfo")}
        </Link>
        <span className="rounded-full bg-ink px-4 py-1.5 text-sm font-semibold text-paper">
          {t("tabMembers")}
        </span>
        <Link
          to="/org/activity"
          className="rounded-full border border-smoke px-4 py-1.5 text-sm font-semibold text-ink hover:bg-mist"
        >
          {t("tabActivity")}
        </Link>
      </nav>

      <Card className="p-0 overflow-hidden">
        {members.isLoading ? (
          <div className="p-6">
            <Skeleton className="h-32 w-full" />
          </div>
        ) : members.isError ? (
          <div className="p-6">
            <ErrorState onRetry={() => members.refetch()} />
          </div>
        ) : !members.data || members.data.items.length === 0 ? (
          <div className="p-6">
            <EmptyState
              icon={<Users className="h-7 w-7" />}
              title={t("memberAddCta")}
            />
          </div>
        ) : (
          <Table>
            <THead>
              <TR>
                <TH>{t("memberFullName")}</TH>
                <TH>{t("memberPhone")}</TH>
                <TH>{t("memberJobTitle")}</TH>
                <TH>Role</TH>
                <TH>{t("memberJoinedAt")}</TH>
                <TH></TH>
              </TR>
            </THead>
            <TBody>
              {members.data.items.map((m) => (
                <TR key={m.membershipId}>
                  <TD>
                    <div className="font-semibold text-ink">
                      {m.fullName ?? "—"}
                    </div>
                    {m.email ? (
                      <div className="text-body-sm text-ash">{m.email}</div>
                    ) : null}
                  </TD>
                  <TD>{m.phone}</TD>
                  <TD>{m.jobTitle ?? "—"}</TD>
                  <TD>
                    <Badge tone={m.role === "OWNER" ? "lime-soft" : "mist"}>
                      {m.role === "OWNER"
                        ? t("memberRoleOwner")
                        : t("memberRoleHr")}
                    </Badge>
                  </TD>
                  <TD>
                    {m.revokedAt ? (
                      <span className="text-body-sm text-ash">
                        {t("memberRevoked")} ·{" "}
                        {new Date(m.revokedAt).toLocaleDateString("vi-VN")}
                      </span>
                    ) : (
                      <span className="text-body-sm text-ink">
                        {new Date(m.joinedAt).toLocaleDateString("vi-VN")}
                      </span>
                    )}
                  </TD>
                  <TD>
                    {isOwner && m.role === "HR" && !m.revokedAt ? (
                      <button
                        type="button"
                        onClick={() => setConfirmRevokeId(m.membershipId)}
                        className="text-body-sm font-semibold text-danger hover:underline"
                      >
                        {t("memberRevoke")}
                      </button>
                    ) : null}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </Card>

      <Dialog
        open={openAdd}
        onClose={() => setOpenAdd(false)}
        title={t("memberAddTitle")}
      >
        <form className="space-y-4" onSubmit={submit} noValidate>
          <FormField
            label={t("memberFullName")}
            error={errors.fullName?.message}
          >
            <Input invalid={!!errors.fullName} {...register("fullName")} />
          </FormField>
          <FormField label={t("memberPhone")} error={errors.phone?.message}>
            <Controller
              control={control}
              name="phone"
              render={({ field }) => (
                <PhoneInput
                  value={field.value}
                  onChange={field.onChange}
                  invalid={!!errors.phone}
                />
              )}
            />
          </FormField>
          <FormField label={t("memberEmail")} error={errors.email?.message}>
            <Input
              type="email"
              invalid={!!errors.email}
              {...register("email")}
            />
          </FormField>
          <FormField
            label={t("memberJobTitle")}
            error={errors.jobTitle?.message}
          >
            <Input invalid={!!errors.jobTitle} {...register("jobTitle")} />
          </FormField>
          <div className="flex justify-end">
            <Button type="submit" loading={add.isPending}>
              {t("memberAddCta")}
            </Button>
          </div>
        </form>
      </Dialog>

      <ConfirmDialog
        open={!!confirmRevokeId}
        onClose={() => setConfirmRevokeId(null)}
        onConfirm={async () => {
          if (!confirmRevokeId) return;
          try {
            await remove.mutateAsync(confirmRevokeId);
            toast({ tone: "success", title: t("memberRevoked") });
            setConfirmRevokeId(null);
          } catch {
            toast({ tone: "error", title: t("saveError") });
          }
        }}
        title={t("memberRevokeTitle")}
        description={t("memberRevokeDesc")}
        confirmLabel={t("memberRevoke")}
        destructive
        loading={remove.isPending}
      />
    </div>
  );
}
